import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import SupportThread from '../components/SupportThread';
import { STUDIO_COPY } from '../config';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import {
  deleteAdminRttsSupportIssue,
  getAdminRttsSupportIssue,
  postAdminRttsSupportMessage,
  remediateRttsSupportIssue,
  restoreAdminRttsSupportIssue,
  updateAdminRttsSupportIssue,
} from '../support/rttsSupportApi';
import type { LscAdminSupportIssue } from '../../lsc/types';

const StudioAdminSupportDetailPage: React.FC = () => {
  useStudioDocumentTitle('Support issue');
  const { issueId } = useParams<{ issueId: string }>();
  const { user } = useUser();
  const [issue, setIssue] = useState<LscAdminSupportIssue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.rtts_admin !== true || !issueId) return;
    let cancelled = false;
    void getAdminRttsSupportIssue(issueId)
      .then((row) => {
        if (!cancelled) setIssue(row);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Not found');
      });
    return () => {
      cancelled = true;
    };
  }, [issueId, user?.rtts_admin]);

  if (user?.rtts_admin !== true) {
    return <Navigate to="/studio" replace />;
  }

  const run = async (action: 'grant_credits' | 'restore_free', amount: number) => {
    if (!issueId) return;
    const ok = window.confirm(
      action === 'grant_credits'
        ? STUDIO_COPY.adminSupportConfirmGrant
        : STUDIO_COPY.adminSupportConfirmRestore
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const payload = await remediateRttsSupportIssue(issueId, action, amount);
      setIssue(payload.issue);
      setResult(
        `Credits ${payload.reel_credit_balance} · free remaining ${payload.reels_remaining}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remediation failed');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: 'resolved' | 'open') => {
    if (!issueId) return;
    setBusy(true);
    try {
      const next = await updateAdminRttsSupportIssue(issueId, { status });
      setIssue(next);
      setResult(status === 'resolved' ? 'Moved to Archived' : 'Reopened');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!issueId) return;
    const ok = window.confirm(STUDIO_COPY.adminSupportConfirmDelete);
    if (!ok) return;
    setBusy(true);
    try {
      const next = await deleteAdminRttsSupportIssue(issueId);
      setIssue(next);
      setResult(STUDIO_COPY.adminSupportDeletedHint);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!issueId) return;
    setBusy(true);
    try {
      const next = await restoreAdminRttsSupportIssue(issueId);
      setIssue(next);
      setResult('Restored');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="studio-panel space-y-4" data-testid="studio-admin-support-detail">
      <Link to="/studio/admin/support" className="text-sm no-underline">
        Back to inbox
      </Link>
      {error ? (
        <p className="text-red-300" role="alert">
          {error}
        </p>
      ) : null}
      {result ? <p data-testid="studio-admin-support-result">{result}</p> : null}
      {issue ? (
        <>
          <h1 className="text-2xl font-semibold">{issue.title}</h1>
          <p className="text-sm text-[var(--studio-muted)]">
            {issue.reporter_uid} · {issue.category} · {issue.status}
            {issue.deleted ? ' · deleted' : ''}
          </p>
          <p className="whitespace-pre-wrap">{issue.description}</p>
          <p className="text-xs text-[var(--studio-muted)]">
            job {issue.context?.job_id || 'n/a'} · tier {issue.context?.tier || 'n/a'} · state{' '}
            {issue.context?.reel_state || 'n/a'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="studio-btn-primary"
              disabled={busy || Boolean(issue.deleted)}
              onClick={() => void run('grant_credits', 1)}
              data-testid="studio-admin-grant-credits"
            >
              {STUDIO_COPY.adminSupportGrant}
            </button>
            <button
              type="button"
              className="studio-btn-outline"
              disabled={busy || Boolean(issue.deleted)}
              onClick={() => void run('restore_free', 1)}
              data-testid="studio-admin-restore-free"
            >
              {STUDIO_COPY.adminSupportRestore}
            </button>
            <button
              type="button"
              className="studio-btn-ghost"
              disabled={busy || Boolean(issue.deleted)}
              onClick={() => void setStatus('resolved')}
              data-testid="studio-admin-resolve"
            >
              {STUDIO_COPY.adminSupportResolve}
            </button>
            <button
              type="button"
              className="studio-btn-ghost"
              disabled={busy || Boolean(issue.deleted)}
              onClick={() => void setStatus('open')}
              data-testid="studio-admin-reopen"
            >
              {STUDIO_COPY.adminSupportReopen}
            </button>
            {issue.deleted ? (
              <button
                type="button"
                className="studio-btn-outline"
                disabled={busy}
                onClick={() => void restore()}
                data-testid="studio-admin-restore-ticket"
              >
                {STUDIO_COPY.adminSupportRestoreTicket}
              </button>
            ) : (
              <button
                type="button"
                className="studio-btn-ghost"
                disabled={busy}
                onClick={() => void remove()}
                data-testid="studio-admin-delete"
              >
                {STUDIO_COPY.adminSupportDelete}
              </button>
            )}
          </div>
          <SupportThread
            messages={issue.messages || []}
            disabled={busy || Boolean(issue.deleted)}
            onSend={async (body) => {
              if (!issueId) return;
              const next = await postAdminRttsSupportMessage(issueId, body);
              setIssue(next);
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default StudioAdminSupportDetailPage;
